import React, { useState } from "react";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  FiUsers,
  FiShoppingBag,
  FiDollarSign,
  FiTrendingUp,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiEye,
  FiEdit,
  FiTrash2,
  FiFilter,
  FiSearch,
  FiDownload,
  FiMapPin,
} from "react-icons/fi";
import { vendorsAPI } from "../../services/api";

const AdminContainer = styled.div`
  padding: 2rem;
`;

const AdminContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
  }

  .content {
    flex: 1;

    .label {
      color: #666;
      font-size: 0.875rem;
      margin-bottom: 0.25rem;
    }

    .value {
      font-size: 2rem;
      font-weight: 700;
      color: #2d5016;
      margin-bottom: 0.25rem;
    }

    .change {
      font-size: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;

      &.positive {
        color: #28a745;
      }

      &.negative {
        color: #dc3545;
      }
    }
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e0e0e0;
  background: white;
  color: #666;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2d5016;
    color: #2d5016;
  }
`;

const SectionContent = styled.div`
  padding: 1.5rem;
`;

const VendorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const VendorCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2d5016;
    box-shadow: 0 2px 8px rgba(45, 80, 22, 0.1);
  }

  .vendor-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 0.75rem;

    .vendor-info {
      h3 {
        font-size: 1rem;
        font-weight: 600;
        color: #2d5016;
        margin-bottom: 0.25rem;
      }

      .cuisine {
        color: #666;
        font-size: 0.875rem;
      }
    }

    .status {
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;

      &.pending {
        background: #fff3cd;
        color: #856404;
      }

      &.approved {
        background: #d4edda;
        color: #155724;
      }

      &.rejected {
        background: #f8d7da;
        color: #721c24;
      }
    }
  }

  .vendor-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    margin-bottom: 1rem;
    font-size: 0.875rem;
    color: #666;

    .detail-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .vendor-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
  }
`;

const VendorActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e0e0e0;
  background: white;
  color: #666;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2d5016;
    color: #2d5016;
  }

  &.approve {
    &:hover {
      border-color: #28a745;
      color: #28a745;
    }
  }

  &.reject {
    &:hover {
      border-color: #dc3545;
      color: #dc3545;
    }
  }
`;

const RecentOrders = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OrderCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;

  .order-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;

    .order-id {
      font-weight: 600;
      color: #2d5016;
    }

    .order-status {
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;

      &.pending {
        background: #fff3cd;
        color: #856404;
      }

      &.confirmed {
        background: #d1ecf1;
        color: #0c5460;
      }

      &.delivered {
        background: #d4edda;
        color: #155724;
      }
    }
  }

  .order-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #666;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;

  h3 {
    margin-bottom: 0.5rem;
    color: #333;
  }
`;

const AdminDashboard = () => {
  const [selectedTab, setSelectedTab] = useState("overview");

  // Fetch real data from API
  const { data: vendorsData, isLoading: vendorsLoading } = useQuery(
    "admin-vendors",
    () => vendorsAPI.getVendors({ status: "pending" }),
    {
      keepPreviousData: true,
    }
  );

  const { data: statsData, isLoading: statsLoading } = useQuery(
    "admin-stats",
    () => vendorsAPI.getAdminStats(),
    {
      keepPreviousData: true,
    }
  );

  const pendingVendors = vendorsData?.data?.data?.vendors || [];
  const adminStats = statsData?.data?.data?.stats || {
    totalVendors: 0,
    pendingVendors: 0,
    activeOrders: 0,
    platformRevenue: 0,
  };

  console.log("Admin Dashboard - Stats data:", statsData);
  console.log("Admin Dashboard - Admin stats:", adminStats);

  const stats = [
    {
      icon: <FiUsers />,
      iconBg: "#2d5016",
      label: "Total Vendors",
      value: adminStats.totalVendors.toString(),
      change: "0%",
      changeType: "neutral",
    },
    {
      icon: <FiShoppingBag />,
      iconBg: "#4a7c59",
      label: "Active Orders",
      value: adminStats.activeOrders.toString(),
      change: "0%",
      changeType: "neutral",
    },
    {
      icon: <FiDollarSign />,
      iconBg: "#28a745",
      label: "Platform Revenue",
      value: `GYD $${adminStats.platformRevenue}`,
      change: "0%",
      changeType: "neutral",
    },
    {
      icon: <FiTrendingUp />,
      iconBg: "#17a2b8",
      label: "Pending Applications",
      value: adminStats.pendingVendors.toString(),
      change: "0%",
      changeType: "neutral",
    },
  ];

  const recentOrders = []; // Will be populated when orders API is ready

  const handleVendorAction = async (vendorId, action) => {
    try {
      if (action === "approve") {
        await vendorsAPI.approveVendor(vendorId);
      } else if (action === "reject") {
        await vendorsAPI.rejectVendor(vendorId);
      }
      // Refresh the data
      window.location.reload();
    } catch (error) {
      console.error(`Error ${action}ing vendor:`, error);
    }
  };

  return (
    <AdminContainer>
      <AdminContent>
        <PageHeader>
          <h1>Admin Dashboard</h1>
          <p>Manage vendors, orders, and platform analytics</p>
        </PageHeader>

        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="icon" style={{ backgroundColor: stat.iconBg }}>
                {stat.icon}
              </div>
              <div className="content">
                <div className="label">{stat.label}</div>
                <div className="value">{stat.value}</div>
                <div className={`change ${stat.changeType}`}>
                  <FiTrendingUp />
                  {stat.change} from last month
                </div>
              </div>
            </StatCard>
          ))}
        </StatsGrid>

        <MainContent>
          <Section>
            <SectionHeader>
              <h2>Pending Vendor Applications</h2>
              <div className="actions">
                <ActionButton>
                  <FiFilter />
                  Filter
                </ActionButton>
                <ActionButton>
                  <FiDownload />
                  Export
                </ActionButton>
              </div>
            </SectionHeader>
            <SectionContent>
              {pendingVendors.length === 0 ? (
                <EmptyState>
                  <h3>No pending applications</h3>
                  <p>All vendor applications have been processed.</p>
                </EmptyState>
              ) : (
                <VendorList>
                  {pendingVendors.map((vendor) => (
                    <VendorCard key={vendor.id}>
                      <div className="vendor-header">
                        <div className="vendor-info">
                          <h3>{vendor.businessName}</h3>
                          <div className="cuisine">{vendor.cuisineType}</div>
                        </div>
                        <div className={`status ${vendor.status}`}>
                          {vendor.status}
                        </div>
                      </div>
                      <div className="vendor-details">
                        <div className="detail-item">
                          <FiMapPin />
                          {vendor.businessAddress}
                        </div>
                        <div className="detail-item">
                          <FiClock />
                          Applied: {vendor.appliedDate}
                        </div>
                      </div>
                      <div className="vendor-actions">
                        <VendorActionButton
                          className="approve"
                          onClick={() =>
                            handleVendorAction(vendor.id, "approve")
                          }
                        >
                          <FiCheckCircle />
                          Approve
                        </VendorActionButton>
                        <VendorActionButton>
                          <FiEye />
                          View
                        </VendorActionButton>
                        <VendorActionButton
                          className="reject"
                          onClick={() =>
                            handleVendorAction(vendor.id, "reject")
                          }
                        >
                          <FiXCircle />
                          Reject
                        </VendorActionButton>
                      </div>
                    </VendorCard>
                  ))}
                </VendorList>
              )}
            </SectionContent>
          </Section>

          <Section>
            <SectionHeader>
              <h2>Recent Orders</h2>
              <div className="actions">
                <ActionButton>
                  <FiSearch />
                  Search
                </ActionButton>
                <ActionButton>
                  <FiDownload />
                  Export
                </ActionButton>
              </div>
            </SectionHeader>
            <SectionContent>
              {recentOrders.length === 0 ? (
                <EmptyState>
                  <h3>No recent orders</h3>
                  <p>Orders will appear here as they come in.</p>
                </EmptyState>
              ) : (
                <RecentOrders>
                  {recentOrders.map((order) => (
                    <OrderCard key={order.id}>
                      <div className="order-header">
                        <div className="order-id">{order.id}</div>
                        <div className={`order-status ${order.status}`}>
                          {order.status}
                        </div>
                      </div>
                      <div className="order-details">
                        <div>Customer: {order.customerName}</div>
                        <div>Vendor: {order.vendorName}</div>
                        <div>Amount: {order.amount}</div>
                        <div>Date: {order.orderDate}</div>
                      </div>
                    </OrderCard>
                  ))}
                </RecentOrders>
              )}
            </SectionContent>
          </Section>
        </MainContent>
      </AdminContent>
    </AdminContainer>
  );
};

export default AdminDashboard;
